import { supabase } from "@/integrations/supabase/client";

export interface PaymentSettings {
  paymentMode: 'manual' | 'stripe' | 'beam';
  stripePublishableKey: string;
  manualQrCodeUrl: string;
  bankName: string;
  bankAccountNumber: string;
  bankAccountName: string;
  beamMerchantId: string;
  beamApiUrl: string;
}

export interface CreateOrderRequest {
  packageId: string;
  credits: number;
  price: number;
  bonus: number;
  packageName?: string;
  guestName?: string;
  guestEmail?: string;
  guestPhone?: string;
}

export interface CreateOrderResponse {
  success: boolean;
  orderId?: string;
  qrCodeUrl?: string;
  expiresAt?: string;
  error?: string;
}

export interface OrderStatus {
  success: boolean;
  orderId?: string;
  status?: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled' | 'expired';
  creditsAmount?: number;
  bonusCredits?: number;
  completedAt?: string;
  expired?: boolean;
  error?: string;
}

/**
 * Fetch payment settings from database
 */
export async function getPaymentSettings(): Promise<PaymentSettings> {
  try {
    // Try to call the RPC function first
    const { data, error } = await supabase.rpc('get_payment_settings');

    if (error) {
      console.error("Error fetching payment settings via RPC:", error);
      // Fallback to direct table query
      return await getPaymentSettingsDirect();
    }

    if (!data) {
      return getDefaultPaymentSettings();
    }

    return {
      paymentMode: parseJsonValue(data.payment_mode, 'manual'),
      stripePublishableKey: parseJsonValue(data.stripe_publishable_key, ''),
      manualQrCodeUrl: parseJsonValue(data.manual_qr_code_url, '/kbank-bas.jpg'),
      bankName: parseJsonValue(data.bank_name, 'KBANK กสิกรไทย'),
      bankAccountNumber: parseJsonValue(data.bank_account_number, '457-2-10138-5'),
      bankAccountName: parseJsonValue(data.bank_account_name, 'รณภพ ชนะพิมพ์'),
      beamMerchantId: parseJsonValue(data.beam_merchant_id, ''),
      beamApiUrl: parseJsonValue(data.beam_api_url, 'https://api.beamcheckout.com'),
    };
  } catch (err) {
    console.error("Error in getPaymentSettings:", err);
    return getDefaultPaymentSettings();
  }
}

/**
 * Direct table query fallback for payment settings
 */
async function getPaymentSettingsDirect(): Promise<PaymentSettings> {
  const { data, error } = await supabase
    .from("payment_settings" as any)
    .select("setting_key, setting_value");

  if (error || !data) {
    return getDefaultPaymentSettings();
  }

  const getValue = (key: string, defaultVal: string): string => {
    const setting = (data as any[]).find(s => s.setting_key === key);
    if (!setting) return defaultVal;
    return parseJsonValue(setting.setting_value, defaultVal);
  };

  return {
    paymentMode: getValue("payment_mode", "manual") as 'manual' | 'stripe' | 'beam',
    stripePublishableKey: getValue("stripe_publishable_key", ""),
    manualQrCodeUrl: getValue("manual_qr_code_url", "/kbank-bas.jpg"),
    bankName: getValue("bank_name", "KBANK กสิกรไทย"),
    bankAccountNumber: getValue("bank_account_number", "457-2-10138-5"),
    bankAccountName: getValue("bank_account_name", "รณภพ ชนะพิมพ์"),
    beamMerchantId: getValue("beam_merchant_id", ""),
    beamApiUrl: getValue("beam_api_url", "https://api.beamcheckout.com"),
  };
}

/**
 * Parse JSON value safely
 */
function parseJsonValue(value: any, defaultVal: string): string {
  if (typeof value === 'string') {
    try {
      // Try to parse if it's a JSON string
      const parsed = JSON.parse(value);
      return typeof parsed === 'string' ? parsed : defaultVal;
    } catch {
      // Return as-is if not JSON
      return value;
    }
  }
  return value || defaultVal;
}

/**
 * Get default payment settings
 */
function getDefaultPaymentSettings(): PaymentSettings {
  return {
    paymentMode: 'manual',
    stripePublishableKey: '',
    manualQrCodeUrl: '/kbank-bas.jpg',
    bankName: 'KBANK กสิกรไทย',
    bankAccountNumber: '457-2-10138-5',
    bankAccountName: 'รณภพ ชนะพิมพ์',
    beamMerchantId: '',
    beamApiUrl: 'https://api.beamcheckout.com',
  };
}

/**
 * Create a Stripe PromptPay order via Edge Function
 */
export async function createStripeOrder(
  request: CreateOrderRequest
): Promise<CreateOrderResponse> {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    const { data, error } = await supabase.functions.invoke('create-promptpay-intent', {
      body: {
        package_id: request.packageId,
        credits: request.credits,
        price: request.price,
        bonus: request.bonus,
        package_name: request.packageName,
        user_id: user?.id,
        guest_name: request.guestName,
        guest_email: request.guestEmail,
        guest_phone: request.guestPhone,
      },
    });

    if (error) {
      console.error("Error creating Stripe order:", error);
      return {
        success: false,
        error: error.message || 'Failed to create payment',
      };
    }

    return {
      success: true,
      orderId: data.order_id,
      qrCodeUrl: data.qr_code_url,
      expiresAt: data.expires_at,
    };
  } catch (err) {
    console.error("Error in createStripeOrder:", err);
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Unknown error',
    };
  }
}

/**
 * Create a manual payment order (for slip upload flow)
 */
export async function createManualOrder(
  request: CreateOrderRequest
): Promise<CreateOrderResponse> {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    const { data, error } = await supabase
      .from("credit_orders" as any)
      .insert({
        user_id: user?.id || null,
        credits_amount: request.credits,
        price_thb: request.price,
        bonus_credits: request.bonus,
        package_name: request.packageName,
        payment_method: 'manual',
        status: 'pending',
        guest_name: request.guestName,
        guest_email: request.guestEmail,
        guest_phone: request.guestPhone,
      })
      .select('id')
      .single();

    if (error) {
      console.error("Error creating manual order:", error);
      return {
        success: false,
        error: error.message || 'Failed to create order',
      };
    }

    return {
      success: true,
      orderId: data.id,
    };
  } catch (err) {
    console.error("Error in createManualOrder:", err);
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Unknown error',
    };
  }
}

/**
 * Update manual order with payment slip URL
 */
export async function updateOrderSlip(
  orderId: string,
  slipUrl: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from("credit_orders" as any)
      .update({
        payment_slip_url: slipUrl,
        status: 'processing', // Move to processing for admin review
      })
      .eq('id', orderId);

    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (err) {
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Unknown error',
    };
  }
}

/**
 * Check order status via RPC function
 */
export async function checkOrderStatus(orderId: string): Promise<OrderStatus> {
  try {
    const { data, error } = await supabase.rpc('check_order_status', {
      p_order_id: orderId,
    });

    if (error) {
      console.error("Error checking order status:", error);
      return { success: false, error: error.message };
    }

    if (!data?.success) {
      return { success: false, error: data?.error || 'Order not found' };
    }

    return {
      success: true,
      orderId: data.order_id,
      status: data.status,
      creditsAmount: data.credits_amount,
      bonusCredits: data.bonus_credits,
      completedAt: data.completed_at,
      expired: data.expired,
    };
  } catch (err) {
    console.error("Error in checkOrderStatus:", err);
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Unknown error',
    };
  }
}

/**
 * Get user's order history
 */
export async function getOrderHistory(limit = 10): Promise<{
  success: boolean;
  orders?: any[];
  error?: string;
}> {
  try {
    const { data, error } = await supabase
      .from("credit_orders" as any)
      .select("*")
      .order("created_at", { ascending: false })
      .limit(limit);

    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true, orders: data || [] };
  } catch (err) {
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Unknown error',
    };
  }
}

/**
 * Create a BeamCheckout PromptPay order via Edge Function
 */
export async function createBeamOrder(
  request: CreateOrderRequest
): Promise<CreateOrderResponse & { qrImageBase64?: string }> {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    // Step 1: Create order record in credit_orders
    const { data: order, error: orderError } = await supabase
      .from("credit_orders" as any)
      .insert({
        user_id: user?.id || null,
        credits_amount: request.credits,
        price_thb: request.price,
        bonus_credits: request.bonus || 0,
        package_name: request.packageName || `${request.credits} Credits`,
        payment_method: 'beam_promptpay',
        status: 'pending',
        guest_name: request.guestName,
        guest_email: request.guestEmail,
        guest_phone: request.guestPhone,
      })
      .select('id')
      .single();

    if (orderError) {
      console.error("Error creating Beam order:", orderError);
      return {
        success: false,
        error: orderError.message || 'Failed to create order',
      };
    }

    const orderId = (order as any).id;

    // Step 2: Call beam-create-charge Edge Function
    const { data, error } = await supabase.functions.invoke('beam-create-charge', {
      body: {
        amount: request.price * 100, // Convert to satang
        currency: 'THB',
        referenceId: `credit-${orderId}`,
        returnUrl: `${window.location.origin}/payment-success?order_id=${orderId}`,
        description: `Credit Purchase - ${request.credits} Credits`,
        customer: {
          email: request.guestEmail || user?.email,
        },
        paymentMethod: {
          paymentMethodType: 'QR_PROMPT_PAY',
          qrPromptPay: {
            expiryTime: new Date(Date.now() + 15 * 60 * 1000).toISOString()
          }
        },
        orderId: orderId,
      },
    });

    if (error) {
      // Log full error details including response body
      console.error("Error creating Beam charge:", error);
      console.error("Error context:", JSON.stringify(error, null, 2));
      console.error("Response data:", data);
      return {
        success: false,
        orderId,
        error: data?.error || error.message || 'Failed to create Beam charge',
      };
    }

    // Extract QR code image from response
    const qrImageBase64 = data?.encodedImage?.imageBase64Encoded
      || data?.qrCode?.imageBase64Encoded
      || data?.encodedImage?.data
      || null;

    const chargeId = data?.chargeId;

    // Update order with Beam charge details
    if (chargeId) {
      await supabase
        .from("credit_orders" as any)
        .update({
          stripe_payment_intent_id: chargeId, // reuse column for beam charge id
          stripe_expires_at: new Date(Date.now() + 15 * 60 * 1000).toISOString(),
        })
        .eq('id', orderId);
    }

    return {
      success: true,
      orderId,
      qrImageBase64,
      qrCodeUrl: qrImageBase64 ? `data:image/png;base64,${qrImageBase64}` : undefined,
      expiresAt: new Date(Date.now() + 15 * 60 * 1000).toISOString(),
    };
  } catch (err) {
    console.error("Error in createBeamOrder:", err);
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Unknown error',
    };
  }
}
